import { ACCESS_CATALOG, ATOMIC_ACTIONS } from "../constants/accessCatalog.constants";
import type { AccessRole } from "../types/access.types";

/**
 * Which bucket a role belongs to in the assign-roles drawer.
 *
 * `module` is the ordinary case — the 84 seeded base roles each grant exactly one `(module, action)`. The
 * other four are the honest answers for roles that do not fit that shape, and none of them is a fallback for
 * "we could not tell": each says something specific.
 */
export type RoleGroupKey =
  | { kind: "module"; moduleId: string }
  /** Spans more than one module — `full_admin_access` and friends. */
  | { kind: "broad" }
  /** An organization's own role whose scope isn't a single catalog module. */
  | { kind: "org" }
  /** No policies, or policies with no statements: assigning it grants nothing. */
  | { kind: "empty" }
  /** Statements over a `resource` the catalog mirror does not know — contract drift. */
  | { kind: "unknown"; resources: string[] };

/**
 * `cards:topup:sensitive` → `cards_topup_sensitive`.
 *
 * Built FROM the catalog rather than by splitting on `_`: several module ids contain underscores of their own
 * (`api_key`, `organization_accounts`), so inverting a slug by splitting would be ambiguous. Looking up an
 * exact stem is not.
 */
const MODULE_BY_SLUG_STEM = new Map<string, string>(
  ACCESS_CATALOG.map((mod) => [mod.id.replace(/:/g, "_"), mod.id]),
);

const CATALOG_IDS = new Set<string>(ACCESS_CATALOG.map((mod) => mod.id));

/** `operations_public_accounts_read` → `operations_public_accounts`; `null` if it ends in no atom. */
function stripActionSuffix(slug: string): string | null {
  for (const action of ATOMIC_ACTIONS) {
    const suffix = `_${action}`;
    if (slug.endsWith(suffix)) return slug.slice(0, -suffix.length);
  }
  return null;
}

/**
 * Resolves the group a role belongs to.
 *
 * The primary signal is the set of distinct `resource` values across its policies' statements — the only
 * thing derived from what the role actually GRANTS. Name and slug are labels; a role can be called anything.
 *
 * A deny-only role lands in its module's group exactly like an allow: the axis here is the resource, not the
 * effect, and the deny marker on the row already carries that distinction.
 *
 * The slug fallback applies only when there is no statement signal at all, and only to GLOBAL roles.
 * Organization roles are slugified from their display name, so their slug says nothing about permissions —
 * guessing from it would mislabel a role on a security screen, which is worse than admitting it is unknown.
 */
export function resolveRoleGroup(role: AccessRole): RoleGroupKey {
  const resources = new Set<string>();
  for (const policy of role.policies) {
    for (const statement of policy.statements) resources.add(statement.resource);
  }

  if (resources.size === 0) {
    if (role.organization_id) return { kind: "org" };
    const stem = stripActionSuffix(role.slug);
    const moduleId = stem ? MODULE_BY_SLUG_STEM.get(stem) : undefined;
    return moduleId ? { kind: "module", moduleId } : { kind: "empty" };
  }

  const known: string[] = [];
  const unknown: string[] = [];
  for (const resource of resources) {
    (CATALOG_IDS.has(resource) ? known : unknown).push(resource);
  }

  // Drift is surfaced, never hidden: a resource the mirror doesn't know still grants real permission, so
  // dropping the role from the list would hide access rather than simplify the view.
  if (known.length === 0) return { kind: "unknown", resources: unknown };
  if (known.length === 1 && unknown.length === 0) return { kind: "module", moduleId: known[0] };
  return { kind: "broad" };
}

/** Stable string key for a group, usable as a map key and a React key. */
export function roleGroupId(group: RoleGroupKey): string {
  switch (group.kind) {
    case "module":
      return `module:${group.moduleId}`;
    case "unknown":
      return "unknown";
    default:
      return group.kind;
  }
}
